import React from 'react';
import { User, Coins, Clock, FileText, Receipt, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Employee {
  id: string;
  name: string;
  totalOwedByCurrency: Record<string, number>;
  pendingByCurrency: Record<string, number>;
  recordedByCurrency: Record<string, number>;
  pendingExpenses: number;
}

interface EmployeeCardProps {
  employee: Employee;
  onViewDetails?: (employeeId: string) => void;
  onMarkPaid?: (employeeId: string) => void;
}

function CurrencyList({ totals, icon: Icon }: { totals: Record<string, number>; icon: React.ElementType }) {
  const entries = Object.entries(totals).filter(([, amt]) => amt > 0);
  if (entries.length === 0) return <span className="text-sm text-muted-foreground">0.00</span>;
  return (
    <>
      {entries.map(([currency, amount]) => (
        <div key={currency} className="flex items-center gap-1 justify-end">
          <Icon className="h-3.5 w-3.5" />
          <span className="font-bold">{amount.toFixed(2)} {currency}</span>
        </div>
      ))}
    </>
  );
}

export function EmployeeCard({ employee, onViewDetails, onMarkPaid }: EmployeeCardProps) {
  // "Mark Paid" only moves *recorded* expenses to paid, so gate the button on whether
  // there are recorded amounts — not total owed (which includes not-yet-recorded pending).
  const hasRecorded = Object.values(employee.recordedByCurrency).some(v => v > 0);

  return (
    <Card className="hover:shadow-medium transition-all duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{employee.name}</h3>
              <p className="text-sm text-muted-foreground">
                {employee.pendingExpenses} pending expense{employee.pendingExpenses !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="text-right space-y-1">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Not recorded</p>
              <CurrencyList totals={employee.pendingByCurrency} icon={Receipt} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Recorded</p>
              <div className="text-accent flex items-center gap-1">
                <CurrencyList totals={employee.recordedByCurrency} icon={FileText} />
                {Object.values(employee.recordedByCurrency).some(v => v > 0) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 flex-shrink-0"
                    onClick={() => {
                      const text = Object.entries(employee.recordedByCurrency)
                        .filter(([, amt]) => amt > 0)
                        .map(([, amt]) => amt.toFixed(2))
                        .join(', ');
                      navigator.clipboard.writeText(text);
                      toast.success('Recorded amount copied');
                    }}
                  >
                    <Copy className="h-3 w-3 text-muted-foreground" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1"
            onClick={() => onViewDetails?.(employee.id)}
          >
            <Clock className="h-4 w-4 mr-1" />
            View Details
          </Button>
          <Button 
            variant="success" 
            size="sm" 
            className="flex-1"
            onClick={() => onMarkPaid?.(employee.id)}
            disabled={!hasRecorded}
          >
            Mark Paid
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
